import Decimal from 'decimal.js';

import type { Agreement } from './schema';

/**
 * Live arithmetic for the fields the owner is currently editing, mirroring
 * `backend/app/validation/arithmetic.py` exactly (same comparison places, same rounding, same
 * four-state `Agreement` vocabulary) so the review screen never disagrees with the server about
 * whether a row adds up. Uses `decimal.js` rather than JS numbers, per `DEPENDENCIES.md`'s "6.2,
 * 6.4" row: a plain JS float would already have lost the exactness CLAUDE.md requires for money
 * by the time it reached this comparison.
 *
 * This module answers "does the draft look consistent right now, before it is saved" - the
 * server's own `ArithmeticResponse` (`review/schema.ts`) remains the answer for the last saved
 * revision, and this module's job is only to give the owner the same feedback live while typing.
 */

/** `backend/app/validation/versions.py`'s `MONEY_COMPARISON_PLACES`. */
export const MONEY_COMPARISON_PLACES = 2;

/** Parses a decimal string exactly as the field holds it, or `null` for empty/invalid input.
 * Never throws: an in-progress keystroke (`"12."`, `"-"`) is treated the same as "no value yet"
 * rather than as an error, which is `decimalInputError`'s job. */
export function parseDecimalOrNull(value: string | null | undefined): Decimal | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  try {
    const parsed = new Decimal(trimmed);
    return parsed.isFinite() ? parsed : null;
  } catch {
    return null;
  }
}

/** `quantity x unit_price`, exact and unrounded - `null` when either factor is missing, matching
 * `LineArithmetic.calculated_amount`. */
export function calculatedLineAmount(
  quantity: string | null,
  unitPrice: string | null,
): Decimal | null {
  const q = parseDecimalOrNull(quantity);
  const p = parseDecimalOrNull(unitPrice);
  if (q === null || p === null) {
    return null;
  }
  return q.times(p);
}

function quantizeMoney(value: Decimal): Decimal {
  return value.toDecimalPlaces(MONEY_COMPARISON_PLACES, Decimal.ROUND_HALF_UP);
}

/** How `calculated` stands against `written`, matching `compare_money` exactly: exact equality
 * first, then equality after centavo rounding, then disagreement; either side missing is
 * `uncheckable` rather than a finding. */
export function compareMoney(written: string | null, calculated: Decimal | null): Agreement {
  const writtenValue = parseDecimalOrNull(written);
  if (writtenValue === null || calculated === null) {
    return 'uncheckable';
  }
  if (calculated.minus(writtenValue).isZero()) {
    return 'agrees';
  }
  if (quantizeMoney(calculated).equals(quantizeMoney(writtenValue))) {
    return 'agrees_after_rounding';
  }
  return 'disagrees';
}

/** The sum of every line amount, or `null` the moment any one of them is missing - matching
 * `ReviewDocument.calculated_total`'s refusal to sum a partial note. An empty note (no rows)
 * also has no total, for the same reason. */
export function sumLineAmounts(amounts: readonly (string | null)[]): Decimal | null {
  if (amounts.length === 0) {
    return null;
  }
  const parsed: Decimal[] = [];
  for (const amount of amounts) {
    const value = parseDecimalOrNull(amount);
    if (value === null) {
      return null;
    }
    parsed.push(value);
  }
  return parsed.reduce((sum, value) => sum.plus(value), new Decimal(0));
}

/** The 1-based positions of every row whose own `quantity x unit_price` disagrees with its
 * written amount - what the note-level total mismatch banner points at, so "el total no
 * cuadra" always names a likely field rather than leaving the owner to hunt for it
 * (CLAUDE.md: "Total mismatches are prominent and point at the likely bad field"). */
export function disagreeingLinePositions(
  lines: readonly {
    readonly quantity: string;
    readonly unitPrice: string;
    readonly lineAmount: string;
  }[],
): readonly number[] {
  const positions: number[] = [];
  lines.forEach((line, index) => {
    const calculated = calculatedLineAmount(
      line.quantity === '' ? null : line.quantity,
      line.unitPrice === '' ? null : line.unitPrice,
    );
    const agreement = compareMoney(line.lineAmount === '' ? null : line.lineAmount, calculated);
    if (agreement === 'disagrees') {
      positions.push(index + 1);
    }
  });
  return positions;
}

export interface DecimalFieldRule {
  readonly maxDigits: number;
  readonly maxDecimalPlaces: number;
}

/** `backend/app/domain/numbers.py`'s `Money`/`Quantity` - identical bounds today, kept as two
 * named constants so a future divergence needs only one edit here. */
export const MONEY_FIELD_RULE: DecimalFieldRule = { maxDigits: 14, maxDecimalPlaces: 4 };
export const QUANTITY_FIELD_RULE: DecimalFieldRule = { maxDigits: 14, maxDecimalPlaces: 4 };

/** Significant digit count the way Python's `Decimal.as_tuple().digits` counts them (and the
 * way Pydantic's `max_digits` reads that tuple): sign and decimal point excluded, leading zeros
 * excluded, trailing zeros included - so `"12.50"` is 4 digits and `"0.5"` is 1. */
function significantDigitCount(trimmed: string): number {
  const withoutSign = trimmed.replace(/^[-+]/, '');
  const withoutPoint = withoutSign.replace('.', '');
  const stripped = withoutPoint.replace(/^0+(?=\d)/, '');
  return stripped.length;
}

/**
 * Spanish validation message for one decimal input, or `null` when it is empty (meaning "not
 * written") or a valid exact decimal string. Deliberately text-based rather than routed through
 * `Decimal` for the digit count: `decimal.js` normalizes away the trailing zeros this business's
 * amounts carry on purpose (`app/domain/numbers.py`: "Both types keep trailing zeros"), so the
 * count has to be taken from what the owner actually typed.
 */
export function decimalInputError(value: string, rule: DecimalFieldRule): string | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return 'Escribe un número, por ejemplo 12.50. Usa punto para los decimales.';
  }
  const [integerPart = '', fractionPart = ''] = trimmed.replace(/^-/, '').split('.');
  if (fractionPart.length > rule.maxDecimalPlaces) {
    return `Como máximo ${String(rule.maxDecimalPlaces)} decimales.`;
  }
  if (significantDigitCount(integerPart + fractionPart) > rule.maxDigits) {
    return 'Ese número tiene demasiados dígitos.';
  }
  return null;
}
